import React, { useState } from 'react';
import { Lock, Eye, EyeOff, Copy, Check, Server, User, KeyRound, Link2, Hash, Tv } from 'lucide-react';

// Carte sécurisée d'affichage des identifiants IPTV.
// Les valeurs sensibles (mot de passe, lien M3U) sont masquées par défaut.
// Aucune valeur n'est envoyée à l'IA : ce composant est purement côté client.
const FIELD_META = {
  server_url: { label: 'URL du serveur', icon: Server, sensitive: false },
  smarters_url: { label: 'URL Smarters', icon: Server, sensitive: false },
  username: { label: "Nom d'utilisateur", icon: User, sensitive: false },
  password: { label: 'Mot de passe', icon: KeyRound, sensitive: true },
  m3u_link: { label: 'Lien M3U', icon: Link2, sensitive: true },
  port: { label: 'Port', icon: Hash, sensitive: false },
  access_name: { label: "Nom de l'accès", icon: Tv, sensitive: false },
};

const CopyButton = ({ value, testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button
      onClick={copy}
      className="flex-shrink-0 p-1.5 rounded-md text-[#B8B8BA] hover:text-[#00FFFF] hover:bg-[#00FFFF]/10 transition-colors"
      aria-label="Copier"
      data-testid={testid}
    >
      {copied ? <Check className="h-3.5 w-3.5 text-[#00FF88]" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
};

const CredentialField = ({ fieldKey, value }) => {
  const meta = FIELD_META[fieldKey] || { label: fieldKey, icon: KeyRound, sensitive: false };
  const Icon = meta.icon;
  const [revealed, setRevealed] = useState(false);
  const display = meta.sensitive && !revealed ? '••••••••••••' : value;
  return (
    <div className="flex items-center gap-2 rounded-lg bg-[#0A0A0B] border border-[#3A3A3D] px-3 py-2" data-testid={`cred-field-${fieldKey}`}>
      <Icon className="h-4 w-4 text-[#FF6B35] flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] text-[#6B6B6E] uppercase tracking-wide">{meta.label}</p>
        <p className={`text-sm text-white truncate ${meta.sensitive && !revealed ? 'tracking-widest' : ''}`} data-testid={`cred-value-${fieldKey}`}>
          {display}
        </p>
      </div>
      {meta.sensitive && (
        <button
          onClick={() => setRevealed((v) => !v)}
          className="flex-shrink-0 p-1.5 rounded-md text-[#B8B8BA] hover:text-[#FF6B35] hover:bg-[#FF6B35]/10 transition-colors"
          aria-label={revealed ? 'Masquer' : 'Afficher'}
          data-testid={`cred-toggle-${fieldKey}`}
        >
          {revealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
        </button>
      )}
      <CopyButton value={value} testid={`cred-copy-${fieldKey}`} />
    </div>
  );
};

export const SecureCredentialCard = ({ credential, profile, subLabel }) => {
  if (!credential) return null;

  // Choix des champs à afficher : si un profil de connexion est fourni, on le respecte.
  let fields;
  if (profile && Array.isArray(profile.required_fields) && profile.required_fields.length) {
    fields = [...profile.required_fields];
    // La source d'URL peut pointer vers smarters_url ou m3u_link.
    if (profile.server_url_source && !fields.includes(profile.server_url_source)) {
      fields = fields.map((f) => (f === 'server_url' ? profile.server_url_source : f));
    }
  } else {
    fields = ['server_url', 'smarters_url', 'username', 'password', 'port', 'm3u_link', 'access_name'];
  }
  const present = fields.filter((f) => credential[f]);

  return (
    <div className="mt-3 rounded-xl border border-[#FF6B35]/30 bg-[#FF6B35]/5 p-3" data-testid="secure-credential-card">
      <div className="flex items-center gap-2 mb-2">
        <Lock className="h-4 w-4 text-[#FF6B35]" />
        <p className="text-white text-sm font-semibold">Vos informations de connexion</p>
      </div>
      {subLabel && <p className="text-[11px] text-[#B8B8BA] mb-2">{subLabel}</p>}

      {profile && (
        <div className="mb-3 rounded-lg bg-[#1A1A1D] border border-[#3A3A3D] p-3">
          <p className="text-xs text-[#FF6B35] font-medium mb-1.5">Configuration {profile.app}</p>
          <ol className="list-decimal list-inside space-y-1 text-xs text-[#B8B8BA]">
            {(profile.instructions || []).map((step, i) => <li key={i}>{step}</li>)}
          </ol>
        </div>
      )}

      <div className="space-y-2">
        {present.map((f) => <CredentialField key={f} fieldKey={f} value={credential[f]} />)}
      </div>

      <p className="text-[10px] text-[#6B6B6E] mt-2 flex items-center gap-1">
        <Lock className="h-3 w-3" /> Ces informations sont confidentielles. Ne les partagez avec personne.
      </p>
    </div>
  );
};

export default SecureCredentialCard;
